import React, { useEffect, useState } from 'react';
import { DatabaseUpcomingPack, DatabaseUpcomingPackModel } from '../../services/DatabaseUpcomingPack';
import { useSoundPacks } from '../../context/SoundPacksContext';
import { isoToDatetimeLocal, datetimeLocalToIso } from '../../helpers/datetimeLocal';
import { formatShortDate } from '../../helpers/formatShortDate';

type UpcomingPackForm = {
    name: string;
    secondaryTitle: string;
    releaseDate: string; // datetime-local value
    public: boolean;
    coverImageUrl: string | null; // currently stored cover
    coverFile: File | null; // newly selected cover
};

function formFromPack(pack: DatabaseUpcomingPack | null): UpcomingPackForm {
    return {
        name: pack?.name ?? '',
        secondaryTitle: pack?.secondaryTitle ?? '',
        releaseDate: isoToDatetimeLocal(pack?.releaseDate),
        public: pack?.public ?? false,
        coverImageUrl: pack?.coverImageUrl ?? null,
        coverFile: null,
    };
}

// Returns the first problem that blocks saving, or null when the form is valid.
function validate(form: UpcomingPackForm): string | null {
    if (!form.name.trim()) return 'Name is required.';
    if (!datetimeLocalToIso(form.releaseDate)) return 'Pick a release date.';
    if (!form.coverFile && !form.coverImageUrl) return 'A cover image is required.';
    return null;
}

function packFromForm(form: UpcomingPackForm): DatabaseUpcomingPack {
    return {
        name: form.name.trim(),
        secondaryTitle: form.secondaryTitle.trim() || null,
        releaseDate: datetimeLocalToIso(form.releaseDate) ?? '',
        public: form.public,
        coverImageUrl: form.coverImageUrl,
    };
}

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500';
const labelClass = 'block text-xs font-medium text-gray-700 mb-1';

function UpcomingPacksEditor() {
    const { soundPacks } = useSoundPacks();
    const [upcomingPacks, setUpcomingPacks] = useState<DatabaseUpcomingPack[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    // The teaser being edited: an id, 'new' for a fresh one, or null (list only).
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState<UpcomingPackForm>(() => formFromPack(null));
    const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const unsub = DatabaseUpcomingPackModel.subscribeUpcomingPacks(
            true,
            (next) => {
                setUpcomingPacks([...next].sort(DatabaseUpcomingPackModel.sortByReleaseDateAsc));
                setLoading(false);
            },
            (err) => {
                setLoadError(err.message);
                setLoading(false);
            },
        );
        return () => unsub();
    }, []);

    // Object URL for the freshly selected cover, revoked when it changes.
    useEffect(() => {
        if (!form.coverFile) {
            setCoverPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(form.coverFile);
        setCoverPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [form.coverFile]);

    const editingPack = editingId && editingId !== 'new' ? upcomingPacks.find(p => p.id === editingId) ?? null : null;
    const validationError = validate(form);
    // The pack uploaded under a teaser's id, if any.
    const releasedPack = (packId: string | undefined) => soundPacks.find(p => p.id === packId);

    const set = <K extends keyof UpcomingPackForm>(key: K, value: UpcomingPackForm[K]) =>
        setForm(prev => ({ ...prev, [key]: value }));

    const startEditing = (pack: DatabaseUpcomingPack | null) => {
        setEditingId(pack?.id ?? 'new');
        setForm(formFromPack(pack));
    };

    const handleSave = async () => {
        if (validationError) return;
        setSaving(true);
        try {
            const pack = packFromForm(form);
            let packId = editingPack?.id;
            if (!packId) {
                packId = await DatabaseUpcomingPackModel.createUpcomingPack(pack);
            }
            if (form.coverFile) {
                pack.coverImageUrl = await DatabaseUpcomingPackModel.uploadCover(packId, form.coverFile);
            }
            await DatabaseUpcomingPackModel.updateUpcomingPack(packId, pack);
            setEditingId(null);
        } catch (error) {
            console.error('Error saving upcoming pack:', error);
            alert('Failed to save: ' + (error instanceof Error ? error.message : 'Unknown error'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (pack: DatabaseUpcomingPack) => {
        if (!pack.id || !window.confirm(`Delete the "${pack.name}" teaser? This can't be undone.`)) return;
        try {
            await DatabaseUpcomingPackModel.deleteUpcomingPack(pack.id);
            if (editingId === pack.id) setEditingId(null);
        } catch (error) {
            console.error('Error deleting upcoming pack:', error);
            alert('Failed to delete: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (loadError) {
        return <p className="text-red-600 text-sm">Failed to load upcoming packs: {loadError}</p>;
    }

    const shownCover = coverPreviewUrl ?? form.coverImageUrl;

    return (
        <div className="space-y-6">
            {editingId === null ? (
                <button
                    onClick={() => startEditing(null)}
                    className="px-4 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                >
                    New upcoming pack
                </button>
            ) : (
                <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {editingPack ? 'Edit upcoming pack' : 'New upcoming pack'}
                    </h3>

                    <div>
                        <label className={labelClass}>Name</label>
                        <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Organic Electronics 2" className={inputClass} disabled={saving} />
                    </div>

                    <div>
                        <label className={labelClass}>Secondary title (optional)</label>
                        <input type="text" value={form.secondaryTitle} onChange={(e) => set('secondaryTitle', e.target.value)} placeholder="City" className={inputClass} disabled={saving} />
                    </div>

                    <div>
                        <label className={labelClass}>Release date (shown as "Coming &lt;date&gt;"; becomes the pack's date when it's uploaded)</label>
                        <input type="datetime-local" value={form.releaseDate} onChange={(e) => set('releaseDate', e.target.value)} className={inputClass} disabled={saving} />
                    </div>

                    <div>
                        <label className={labelClass}>
                            Cover image (PNG). Use the final pack cover: the plugin caches it on disk and keeps showing it after release.
                        </label>
                        {shownCover && (
                            <img src={shownCover} alt="Cover" className="mb-2 w-32 h-32 object-cover rounded-md border border-gray-200" />
                        )}
                        <input
                            type="file"
                            accept="image/png"
                            onChange={(e) => set('coverFile', e.target.files?.[0] || null)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            disabled={saving}
                        />
                    </div>

                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={form.public} onChange={(e) => set('public', e.target.checked)} disabled={saving} />
                        Public (unchecked = visible to admins only)
                    </label>

                    {validationError && (
                        <p className="text-red-600 font-medium text-xs">{validationError}</p>
                    )}

                    <div className="flex gap-3">
                        <button
                            onClick={handleSave}
                            disabled={saving || !!validationError}
                            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium ${saving || validationError
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                            onClick={() => setEditingId(null)}
                            disabled={saving}
                            className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {upcomingPacks.length === 0 ? (
                <p className="text-gray-500 text-sm">No upcoming packs.</p>
            ) : (
                <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
                    {upcomingPacks.map(pack => {
                        const released = releasedPack(pack.id);
                        return (
                            <div key={pack.id} className="flex items-center gap-4 p-3">
                                {pack.coverImageUrl ? (
                                    <img src={pack.coverImageUrl} alt="" className="w-12 h-12 object-cover rounded shrink-0" />
                                ) : (
                                    <div className="w-12 h-12 rounded bg-gray-200 shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 truncate">
                                        {pack.name}{pack.secondaryTitle && <span className="font-normal text-gray-500"> · {pack.secondaryTitle}</span>}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        Coming {formatShortDate(pack.releaseDate)}
                                        {released && <> · uploaded as "{released.name}"{released.public ? ', public — safe to delete' : ' (preview)'}</>}
                                    </p>
                                </div>
                                {released && (
                                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Released</span>
                                )}
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${pack.public ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                    {pack.public ? 'Public' : 'Private'}
                                </span>
                                <button
                                    onClick={() => startEditing(pack)}
                                    disabled={saving}
                                    className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleDelete(pack)}
                                    disabled={saving}
                                    className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                                >
                                    Delete
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default UpcomingPacksEditor;
